/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Field, Formik } from "formik";
import _, { camelCase, uniqueId } from 'lodash';

import { FitinsurAPI, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';

import ResponsiveTable from 'components/ResponsiveTable';
import Button, { ButtonContainer } from 'components/Button';
import ComboBox from 'components/Form/ComboBox3';
import { extensionsAllowed } from './extensionsAllowed';

import { TagInput, DragZoneContainer, DropZoneArea, TableContainer } from './styles';

const DragZoneUpload = ({
  documentTypeRequired,
  documentType,
  fullHeight,
  autoUpload,
  idOperation,
  onSuccess,
  isDraft,
  isInternalDocument,
  options,
  s3upload,
  idPersonInsurer,
  changeShowAttachments,
  showAttachmentsState,
  changeAttachments,
  height,
  disabled,
  idDomain,
  idDomainStatus,
  checkExtensionFile,
  multiple
}) => {

  const mc = new MessageCenter();
  const fitinsur = new FitinsurAPI();

  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const onDragOver = () => setIsDragging(true);

  const onDragEnter = () => setIsDragging(true);

  const onDragLeave = () => setIsDragging(false);

  const onDrop = () => setIsDragging(false);

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({ onDragOver, onDragEnter, onDragLeave, onDrop, disabled: disabled, multiple });

  useEffect(() => {
    if (autoUpload && acceptedFiles && acceptedFiles.length && !s3upload) uploadFiles(acceptedFiles, documentType, isInternalDocument);
    else if (autoUpload && acceptedFiles && acceptedFiles.length && s3upload) uploadS3Files(acceptedFiles, documentType, isInternalDocument, idDomain, idDomainStatus);
    else if (!autoUpload && acceptedFiles && acceptedFiles.length) addPendingFiles(acceptedFiles);
  }, [acceptedFiles])

  const addPendingFiles = async (files) => {
    const fileObjects = files.map(file => ({ file, type: file.type, name: file.name, folder: "", id: uniqueId() }))
    setPendingFiles(fileObjects);
  }

  const confirmPendingFiles = async () => {
    const uploadPromises = []
    for (const pendingFile of pendingFiles) {
      uploadPromises.push(uploadFiles([pendingFile.file], pendingFile.documentType, pendingFile.isInternalDocument, pendingFile.folder))
    }

    const responses = await Promise.all(uploadPromises);
    loading.stop();
    onSuccess && onSuccess(responses.map(v => v && v[0]).filter(v => v))
    setPendingFiles([])
  }

  const uploadFiles = async (files, documentType, isInternalDocument = false, folder = "") => {
    try {
      if (checkExtensionFile && files) {
        let extension = files[0].name.split('.').pop();
        if (!extensionsAllowed.includes(extension)) throw new Error('Only files .xlsx are allowed!');
      }

      if (documentTypeRequired && !documentType) throw new Error("Please, specify the document type.");
      if (!files || !files[0]) throw new Error("Invalid file, please select a valid one.");

      loading.start();
      const formData = new FormData();

      for (const file of files) formData.append('files', file)
      formData.append('idOperation', idOperation || "");
      formData.append('idTypeDocument', documentType?.idAnswerOption || "")
      formData.append('dsTypeDocument', documentType?.dsAnswerOption || "")
      formData.append('folder', folder)
      formData.append('idPersonInsurer', idPersonInsurer || "")
      formData.append('resultIdDomainDocument', idDomain || "")
      formData.append('resultIdDomainStatusDocument', idDomainStatus || "")

      if (isDraft) formData.append('isDraft', "true")
      if (isInternalDocument) formData.append('isInternalDocument', "true")

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestFileUpload(formData).then(response => response.json());
      if (response.ret_code !== 200) throw new Error(response.ret_msg_details)

      if (documentTypeRequired) return response.ret_value;

      loading.stop();
      onSuccess && onSuccess(response.ret_value);
    } catch (err) {
      console.log('UploadButton - error', err);
      mc.error(err.message);
      loading.stop()
    }
  }

  const uploadS3Files = async (files, documentType, isInternalDocument = false, folder = "") => {
    try {
      if (documentTypeRequired && !documentType) throw new Error("Please, specify the document type.");
      if (!files || !files[0]) throw new Error("Invalid file, please select a valid one.");

      loading.start();
      const formData = new FormData();

      for (const file of files) formData.append('files', file)

      formData.append('idOperation', idOperation || "");
      formData.append('idTypeDocument', documentType?.idAnswerOption || "")
      formData.append('dsTypeDocument', documentType?.dsAnswerOption || "")
      formData.append('folder', folder)
      formData.append('idPersonInsurer', idPersonInsurer || "")
      formData.append('resultIdDomainDocument', idDomain || "")
      formData.append('resultIdDomainStatusDocument', idDomainStatus || "")

      if (isDraft) formData.append('isDraft', "true")
      if (isInternalDocument) formData.append('isInternalDocument', "true")

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestFileUploadS3(formData).then(response => response.json());
      if (response.ret_code !== 200) throw new Error(response.ret_msg_details)

      if (documentTypeRequired) return response.ret_value;

      loading.stop();
      onSuccess && onSuccess(response.ret_value);
    } catch (err) {
      console.log('UploadButton - error', err);
      mc.error(err.message);
      loading.stop()
    }
  }

  const columns = [
    {
      title: "Name",
      key: "name",
      minWidth: 100,
      maxWidth: 350
    },
    {
      title: "Type",
      key: "type",
      minWidth: 150,
      maxWidth: 280,
      component: (row) => <Field
        name={`documentType-${row.id}`}
        type="text"
        topReduce
        isRequired={false}
        component={ComboBox}
        options={options || []}
        validation={false}
        searchList={[]}
        onChange={(value) => {
          setPendingFiles(pendingFiles.map(file => file.id !== row.id ? file : {
            ...file,
            documentType: value,
            isInternalDocument: value.behavior && value.behavior.folder === "Internal Documents",
            folder: value.behavior.folder
          }))
        }}
      />
    },
    {
      title: "Folder",
      key: "folder",
      minWidth: 120
    },
    {
      title: "Remove",
      key: "remove",
      minWidth: 120
    }
  ];

  if (changeAttachments) changeAttachments(pendingFiles);

  const changeRemoveAttachments = state => setPendingFiles(state)

  return (
    <>
      {!pendingFiles.length ?
        <DragZoneContainer isDragging={isDragging} fullHeight={fullHeight} height={height}>
          <DropZoneArea {...getRootProps({ className: 'dropzone' })}>
            <p>Drag and Drop Documents here to upload</p>
            <TagInput {...getInputProps()} />
          </DropZoneArea>
        </DragZoneContainer>
        :
        <TableContainer>
          <ResponsiveTable columns={columns} data={pendingFiles} changeShowAttachments={changeShowAttachments} changeRemoveAttachments={changeRemoveAttachments} />
          <ButtonContainer style={{ justifyContent: 'flex-end' }}>
            {changeShowAttachments && (
              <Button
                style={{ margin: 15 }}
                onClick={() => changeShowAttachments(!showAttachmentsState)}
                secundary
              >
                CANCEL
              </Button>
            )}

            <Button
              style={{ margin: 15 }}
              onClick={(e) => confirmPendingFiles()}
              primary
            >
              CONFIRM
            </Button>
          </ButtonContainer>
        </TableContainer>
      }
    </>
  );
};


export default DragZoneUpload;
